import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { getOrderSession, switchOrder, getSchemes } from "../../../api/bseApi";

const SWITCH_TYPES = [
  { value: "FULL", label: "Full Switch", allRedeem: "Y" },
  { value: "PARTIAL", label: "Partial Switch", allRedeem: "N" },
];
const PARTIAL_BY = [
  { value: "AMOUNT", label: "Amount (OrderVal)" },
  { value: "UNITS", label: "Units (Qty)" },
];
const DP_MODES = [
  { value: "P", label: "Physical (P)" },
  { value: "C", label: "CDSL (C)" },
  { value: "N", label: "NSDL (N)" },
];

/**
 * SwitchOrderScreen - BSE StAR MF Switch Order Entry
 * Opens from Portfolio / Holdings screen
 * Receives: ClientCode, Source Scheme list, Folio numbers, Target Scheme list (same AMC)
 */
export default function SwitchOrderScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const params = new URLSearchParams(location.search);
  const state = location.state;
  const [context, setContext] = useState(null);
  const [schemes, setSchemes] = useState([]);
  const [form, setForm] = useState({
    fromSchemeCd: "",
    fromSchemeCdManual: "",
    toSchemeCd: "",
    toSchemeCdManual: "",
    folioNo: "",
    switchType: "FULL",
    partialBy: "AMOUNT",
    amount: "",
    units: "",
    dptxn: "P",
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode || params.get("clientCode");
    if (clientCode) {
      setContext({
        clientCode,
        sourceSchemes: state?.sourceSchemes,
        targetSchemes: state?.targetSchemes,
        folios: state?.folios,
      });
      setFetching(false);
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available. Complete UCC creation first.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({
            clientCode: String(ucc).trim(),
            sourceSchemes: state?.sourceSchemes,
            targetSchemes: state?.targetSchemes,
            folios: state?.folios,
          });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire("Error", "Missing ClientCode. Open from Portfolio or User Listing.", "error").then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, location.search, userId, navigate]);

  useEffect(() => {
    const loadSchemes = async () => {
      try {
        const res = await getSchemes();
        if (res.data?.status === 1 && Array.isArray(res.data?.data)) {
          setSchemes(res.data.data);
        }
      } catch (err) {
        console.warn("Scheme list load failed:", err.message);
      }
    };
    loadSchemes();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const validate = () => {
    const err = {};
    const fromScheme = (form.fromSchemeCd || form.fromSchemeCdManual || "").trim();
    const toScheme = (form.toSchemeCd || form.toSchemeCdManual || "").trim();
    if (!fromScheme) err.fromSchemeCd = "Source Scheme is mandatory";
    if (!toScheme) err.toSchemeCd = "Target Scheme is mandatory";
    if (fromScheme && toScheme && fromScheme === toScheme) err.toSchemeCd = "Target scheme must be different from source";
    if (!form.folioNo?.trim()) err.folioNo = "FolioNo is mandatory";
    if (form.switchType === "PARTIAL") {
      if (form.partialBy === "AMOUNT") {
        const amt = parseFloat(form.amount);
        if (!form.amount || isNaN(amt) || amt <= 0) err.amount = "Amount is mandatory for partial switch by amount";
        if (form.units?.trim()) err.units = "Do not enter Units when switching by Amount";
      } else {
        const qty = parseFloat(form.units);
        if (!form.units || isNaN(qty) || qty <= 0) err.units = "Units is mandatory for partial switch by units";
        if (form.amount?.trim()) err.amount = "Do not enter Amount when switching by Units";
      }
    } else {
      if (form.amount?.trim() || form.units?.trim()) err.amount = "Amount and Units must be empty for Full Switch";
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const sessionRes = await getOrderSession();
      if (sessionRes.data?.status !== 1) {
        const msg = sessionRes.data?.error || sessionRes.data?.message || "Session failed";
        Swal.fire("Error", msg, "error");
        setLoading(false);
        return;
      }

      const { encryptedPassword, passKey } = sessionRes.data.data;
      const fromSchemeCd = (form.fromSchemeCd || form.fromSchemeCdManual || "").trim();
      const toSchemeCd = (form.toSchemeCd || form.toSchemeCdManual || "").trim();

      const isFull = form.switchType === "FULL";
      let switchAmount = "";
      let switchUnits = "";
      if (!isFull) {
        if (form.partialBy === "AMOUNT") switchAmount = parseFloat(form.amount) || "";
        else switchUnits = form.units?.trim() || "";
      }

      const payload = {
        encryptedPassword,
        passKey,
        clientCode: context.clientCode,
        fromSchemeCd,
        toSchemeCd,
        buySell: "SO",
        dptxn: form.dptxn,
        switchAmount,
        switchUnits,
        allUnitsFlag: isFull ? "Y" : "N",
        folioNo: form.folioNo.trim(),
        kycStatus: "Y",
        minRedeem: "N",
      };

      const res = await switchOrder(payload);

      if (res.data?.status === 1) {
        const orderId = res.data?.data?.soOrderId || res.data?.data?.siOrderId;
        const bseFailed = res.data?.bseFailed === true;
        const payAmount = isFull ? 0 : (form.partialBy === "AMOUNT" ? parseFloat(form.amount) : 0);
        Swal.fire({
          icon: bseFailed ? "warning" : "success",
          title: "Switch Order Placed",
          text: bseFailed
            ? (res.data?.message || "Switch order recorded locally. BSE could not process; you may retry later.")
            : `Switch order placed successfully. Order ID: ${orderId || "N/A"}`,
        });
        if (payAmount > 0) {
          navigate("/payment/mode", { state: { orderNo: orderId, clientCode: context.clientCode, amount: payAmount } });
        } else {
          navigate("/user");
        }
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "Switch order failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        err.message ||
        "Switch order failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const useManualScheme = schemes.length === 0;
  const isFull = form.switchType === "FULL";

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                <li className="breadcrumb-item"><Link to="/user">Users</Link></li>
                <li className="breadcrumb-item active">Switch Order</li>
              </ol>
            </nav>
            <h5>Switch Order</h5>
            <p className="text-muted mb-0">Client Code: {context.clientCode}</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                Switch Details
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Source Scheme (SchemeCd) <span className="text-danger">*</span></label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.fromSchemeCd ? "is-invalid" : ""}`}
                      name="fromSchemeCdManual"
                      value={form.fromSchemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter source scheme code"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.fromSchemeCd ? "is-invalid" : ""}`}
                      name="fromSchemeCd"
                      value={form.fromSchemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Source Scheme</option>
                      {schemes.map((s) => (
                        <option key={s.schemeCd} value={s.schemeCd}>
                          {s.schemeName || s.schemes || s.schemeCd} ({s.schemeCd})
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.fromSchemeCd && <div className="invalid-feedback">{errors.fromSchemeCd}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Target Scheme (TargetSchemeCd) <span className="text-danger">*</span></label>
                  {useManualScheme ? (
                    <input
                      type="text"
                      className={`form-control ${errors.toSchemeCd ? "is-invalid" : ""}`}
                      name="toSchemeCdManual"
                      value={form.toSchemeCdManual}
                      onChange={handleChange}
                      placeholder="Enter target scheme code (same AMC)"
                    />
                  ) : (
                    <select
                      className={`form-select ${errors.toSchemeCd ? "is-invalid" : ""}`}
                      name="toSchemeCd"
                      value={form.toSchemeCd}
                      onChange={handleChange}
                    >
                      <option value="">Select Target Scheme (same AMC)</option>
                      {schemes
                        .filter((s) => s.schemeCd !== (form.fromSchemeCd || form.fromSchemeCdManual))
                        .map((s) => (
                          <option key={s.schemeCd} value={s.schemeCd}>
                            {s.schemeName || s.schemes || s.schemeCd} ({s.schemeCd})
                          </option>
                        ))}
                    </select>
                  )}
                  {errors.toSchemeCd && <div className="invalid-feedback">{errors.toSchemeCd}</div>}
                  <small className="text-muted">Target scheme must be from same AMC as source</small>
                </div>
                <div className="col-md-6">
                  <label className="form-label">Folio No (FolioNo) <span className="text-danger">*</span></label>
                  <input
                    type="text"
                    className={`form-control ${errors.folioNo ? "is-invalid" : ""}`}
                    name="folioNo"
                    value={form.folioNo}
                    onChange={handleChange}
                    placeholder="e.g. 12345678"
                  />
                  {errors.folioNo && <div className="invalid-feedback">{errors.folioNo}</div>}
                </div>
                <div className="col-md-6">
                  <label className="form-label">Switch Type</label>
                  <select
                    className="form-select"
                    name="switchType"
                    value={form.switchType}
                    onChange={handleChange}
                  >
                    {SWITCH_TYPES.map((t) => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
                {!isFull && (
                  <>
                    <div className="col-md-6">
                      <label className="form-label">Partial By</label>
                      <select
                        className="form-select"
                        name="partialBy"
                        value={form.partialBy}
                        onChange={handleChange}
                      >
                        {PARTIAL_BY.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div className="col-md-6">
                      {form.partialBy === "AMOUNT" ? (
                        <>
                          <label className="form-label">Amount (OrderVal) <span className="text-danger">*</span></label>
                          <input
                            type="number"
                            className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                            name="amount"
                            value={form.amount}
                            onChange={handleChange}
                            min="0"
                            step="0.01"
                            placeholder="e.g. 10000"
                          />
                          {errors.amount && <div className="invalid-feedback">{errors.amount}</div>}
                        </>
                      ) : (
                        <>
                          <label className="form-label">Units (Qty) <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            className={`form-control ${errors.units ? "is-invalid" : ""}`}
                            name="units"
                            value={form.units}
                            onChange={handleChange}
                            placeholder="e.g. 100.50"
                          />
                          {errors.units && <div className="invalid-feedback">{errors.units}</div>}
                        </>
                      )}
                    </div>
                  </>
                )}
                <div className="col-md-6">
                  <label className="form-label">DP Transaction Mode (DPTxn)</label>
                  <select
                    className="form-select"
                    name="dptxn"
                    value={form.dptxn}
                    onChange={handleChange}
                  >
                    {DP_MODES.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="d-flex gap-2 justify-content-end mt-4 pt-3" style={{ borderTop: "1px solid #e6eef0" }}>
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Placing...
                    </>
                  ) : (
                    "Place Switch Order"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
